import { __decorate } from "tslib";
import 'element-internals-polyfill';
import { html, css, LitElement, nothing } from 'lit';
import { when } from 'lit/directives/when.js';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state, query, queryAssignedElements, } from 'lit/decorators.js';
import { autoUpdate, computePosition, flip, offset, size, } from '@floating-ui/dom';
import { getNextElementOnKey } from './helpers/menuHelpers.js';
import { Size } from './enums/size.js';
import { InputVariant } from './enums/input-variant.js';
import './viedoc-icon.js';
import './viedoc-tag.js';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Large, ...SelectSize } = Size;
export { InputVariant };
const DROPDOWN_MAX_HEIGHT = 18.75; // rem (300px)
const DROPDOWN_MIN_WIDTH = 20; // rem (320px), will shrink below this if there is not enough space
const getHtmlFontSize = () => parseFloat(getComputedStyle(document.documentElement).fontSize);
const VALUE_DELIMITER = ',';
export { SelectSize };
let ViedocSelect = class ViedocSelect extends LitElement {
    constructor() {
        super();
        this.disabled = false;
        this.label = '';
        this.multiple = false;
        this.name = '';
        this.variant = InputVariant.Outline;
        this.size = SelectSize.Medium;
        // Current value. Submitted with the form as part of a name/value pair.
        // If `multiple` is true, value attribute is `delimiter` list of selected options
        // and the value property is an array.
        this.value = '';
        this.isOpen = false;
        this._internals = this.attachInternals();
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
    }
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener('click', this.handleDocumentClick);
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('value')) {
            this.syncSelectState();
        }
    }
    firstUpdated() {
        this.cleanup = autoUpdate(this.combobox, this.dropdown, () => {
            this.updateDropdownPosition();
        });
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.cleanup) {
            this.cleanup();
        }
        document.removeEventListener('click', this.handleDocumentClick);
    }
    get values() {
        return Array.isArray(this.value) ? [...this.value] : [this.value];
    }
    get selectedItems() {
        return this.menuItems.filter(item => item.selected);
    }
    /**
     * Sync value property to match menu item selection
     */
    syncValue() {
        var _a, _b;
        if (this.multiple) {
            this.value = this.selectedItems.map(selectedItem => selectedItem.value);
        }
        else {
            this.value = (_b = (_a = this.selectedItems[0]) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '';
        }
    }
    /**
     * Sync menu item selection to match value property
     */
    syncSelectState() {
        let isChanged = false;
        const selectedValues = this.value ? this.values : [];
        this._internals.setFormValue(this.value ? this.values.join(VALUE_DELIMITER) : null);
        this.menuItems.forEach(item => {
            const isSelected = selectedValues.includes(item.value);
            isChanged = isChanged || item.selected !== isSelected;
            // eslint-disable-next-line no-param-reassign
            item.selected = isSelected;
        });
        if (isChanged) {
            this.requestUpdate();
        }
    }
    updateMenuMaxHeight(availableHeight) {
        const preferedMaxHeight = DROPDOWN_MAX_HEIGHT * getHtmlFontSize();
        this.style.setProperty('--viedoc-menu-max-height', `${Math.min(availableHeight, preferedMaxHeight)}px`);
    }
    updateDropdownWidth(availableWidth, rects) {
        const preferedMinWidth = DROPDOWN_MIN_WIDTH * getHtmlFontSize();
        Object.assign(this.dropdown.style, {
            width: `${Math.max(preferedMinWidth, rects.reference.width)}px`,
            maxWidth: `${availableWidth}px`,
        });
    }
    updateDropdownPosition() {
        computePosition(this.combobox, this.dropdown, {
            middleware: [
                offset(17),
                flip({ padding: 10 }),
                size({
                    padding: 10,
                    apply: ({ availableHeight, availableWidth, rects }) => {
                        this.updateDropdownWidth(availableWidth, rects);
                        this.updateMenuMaxHeight(availableHeight);
                    },
                }),
            ],
            placement: 'bottom-start',
            strategy: 'fixed',
        }).then(({ x, y }) => {
            Object.assign(this.dropdown.style, {
                left: `${x}px`,
                top: `${y}px`,
            });
        });
    }
    focusOnMenuItem() {
        const currentItem = this.selectedItems[0] || this.menuItems[0];
        if (currentItem) {
            this.menu.setCurrentItem(currentItem);
            currentItem.focus();
        }
    }
    addValue(itemValue) {
        const componentValues = this.values;
        if (!componentValues.includes(itemValue)) {
            this.value = [...componentValues, itemValue];
        }
    }
    removeValue(itemValue) {
        const componentValues = this.values;
        const itemIndex = componentValues.indexOf(itemValue);
        if (itemIndex >= 0) {
            componentValues.splice(itemIndex, 1);
            this.value = componentValues;
        }
    }
    toggleValue(itemValue) {
        const componentValues = this.values;
        const itemIndex = componentValues.indexOf(itemValue);
        if (itemIndex < 0) {
            this.value = [...componentValues, itemValue];
        }
        else {
            componentValues.splice(itemIndex, 1);
            this.value = componentValues;
        }
    }
    async toggleOpen(force) {
        this.isOpen = force !== undefined ? force : !this.isOpen;
        await this.updateComplete;
        if (this.isOpen) {
            this.updateDropdownPosition();
            this.focusOnMenuItem();
        }
    }
    /**
     * Handle menu item change, triggered by setting the selected attribute on menu item
     *
     * Update value to reflect item selection.
     */
    menuItemChanged(menuItem) {
        if (this.multiple) {
            if (menuItem.selected) {
                this.addValue(menuItem.value);
            }
            else {
                this.removeValue(menuItem.value);
            }
        }
        else if (menuItem.selected) {
            this.value = menuItem.value;
        }
    }
    /**
     * Handle menu item click.
     *
     * If this is a multi select, toggle value. Otherwise ensure to close select and simply set value.
     */
    async menuItemClicked(menuItem) {
        let isChanged = true;
        if (this.multiple) {
            this.toggleValue(menuItem.value);
        }
        else {
            if (this.isOpen) {
                this.toggleOpen(false);
                this.combobox.focus();
            }
            isChanged = this.value !== menuItem.value;
            this.value = menuItem.value;
        }
        if (isChanged) {
            await this.updateComplete;
            this.dispatchEvent(new Event('change'));
        }
    }
    /**
     * Handle menu item selection, either by click on menu or by setting the selected attribute on menu item
     *
     * If the event is triggered by attribute change, event.preventChangeEvent is set (to true) and
     * no change event should be dispatched. This also means that item already has corrent selected
     * state and should be set to reflect this (i.e. add/remove instead of toggle)
     */
    handleMenuSelect(event) {
        const { detail: { item, preventChangeEvent }, } = event;
        // Event is triggered when menu item selected attribute is updated
        if (preventChangeEvent) {
            this.menuItemChanged(item);
        }
        else {
            this.menuItemClicked(item);
        }
    }
    handleDocumentClick(event) {
        const target = event.target;
        const isClickoutside = target.closest('viedoc-select') !== this;
        if (this.isOpen && isClickoutside) {
            this.toggleOpen(false);
        }
    }
    handleComboboxClick() {
        this.toggleOpen();
    }
    async handleComboboxKeydown(event) {
        if (this.disabled) {
            return;
        }
        const { code } = event;
        if (['Enter', 'Space'].includes(code)) {
            event.preventDefault();
            this.toggleOpen();
        }
        // Only for single selection: step enabled items without opening dropdown
        if (!this.multiple &&
            ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(code)) {
            event.preventDefault();
            const enabledItems = this.menuItems.filter(item => !item.disabled);
            const selectedItem = this.menuItems.find(item => item.value === this.value);
            const nextItem = getNextElementOnKey(code, enabledItems, selectedItem);
            nextItem === null || nextItem === void 0 ? void 0 : nextItem.click(); // simulate click event to select item;
        }
    }
    /**
     * Close dropdown on escape or tab
     */
    handleDropdownKeydown(event) {
        const { code } = event;
        if (['Escape', 'Tab'].includes(code)) {
            event.preventDefault();
            this.toggleOpen(false);
            this.combobox.focus();
        }
    }
    async handleMenuSlotChange() {
        // Ensure menu items use check marks if multiple mode select
        if (this.multiple) {
            for (const item of this.menuItems.filter(menuItem => !menuItem.checkMark)) {
                item.checkMark = true;
            }
        }
        // Wait for items to render before gathering labels otherwise the slot won't exist
        await Promise.all(this.menuItems.map(item => item.render));
        // This fixes the issue with first item stays 'select' even though other
        // items are added with the selected attribute set to true.
        //  If an item has been selected previously, the attribute 'select' is not set
        // which means that the 'select' poperty has to be reset
        const itemsToReset = this.menuItems.filter(item => item.selected === true && item.getAttribute('selected') === null);
        itemsToReset.forEach(item => {
            // eslint-disable-next-line no-param-reassign
            item.selected = false;
        });
        // If we are in single select, and there is no selection
        if (!this.multiple &&
            this.menuItems.every(item => item.getAttribute('selected') === null)) {
            // Select first enabled item if any
            const firstEnabledItemIndex = this.menuItems.findIndex(item => !item.disabled);
            if (firstEnabledItemIndex >= 0) {
                this.menuItems[firstEnabledItemIndex].selected = true;
            }
        }
        this.syncValue();
        this.requestUpdate();
    }
    handleMenuLabelChange(event) {
        const { item } = event.detail;
        if (this.selectedItems.some(selectedItem => selectedItem.value === item.value)) {
            // Request update since text content change on menu item might not trigger re-render and thus will not update tags
            this.requestUpdate();
        }
    }
    /**
     * Remove mutli-select value when viedoc tag remove button is clicked
     */
    async handleRemoveClick(event, value) {
        event.stopPropagation();
        this.toggleValue(value);
        await this.updateComplete;
        this.dispatchEvent(new Event('change'));
    }
    get tags() {
        return this.selectedItems.map(menuItem => html `<viedoc-tag
          size="${this.size}"
          removable
          @tag-remove=${(event) => this.handleRemoveClick(event, menuItem.value)}
        >
          ${menuItem.getTextLabel()}</viedoc-tag
        >`);
    }
    get selectedValue() {
        const currentItem = this.selectedItems[0];
        if (!currentItem) {
            return nothing;
        }
        // Clone prefix/suffix element to prevent moving them
        const labelPrefix = currentItem.prefixContent.map(e => e.cloneNode(true));
        const labelSuffix = currentItem.suffixContent.map(e => e.cloneNode(true));
        return html `
      ${when(labelPrefix.length, () => html `<span class="select__label--prefix">${labelPrefix}</span>`)}
      <span
        class=${classMap({
            'select__label--content': true,
            secondary: currentItem.secondary,
        })}
      >
        ${currentItem.getTextLabel()}
      </span>
      ${when(labelSuffix.length, () => html `<span class="select__label--suffix">${labelSuffix}</span>`)}
    `;
    }
    render() {
        const variant = this.multiple ? InputVariant.Outline : this.variant;
        const classes = classMap({
            select: true,
            'select--open': this.isOpen,
            'select--disabled': this.disabled,
            [`select--${this.size}`]: true,
            [`select--${variant}`]: true,
            'select--multiple': this.multiple,
        });
        return html `
      <div class=${classes}>
        <div
          class="select__combobox"
          role="combobox"
          aria-disabled=${this.disabled}
          aria-expanded=${this.isOpen}
          aria-controls="menu"
          aria-label=${this.label}
          aria-haspopup="listbox"
          tabindex="0"
          @click=${this.handleComboboxClick}
          @keydown=${this.handleComboboxKeydown}
        >
          <span class="select__label">
            ${this.multiple ? this.tags : this.selectedValue}
          </span>
          <viedoc-icon name="arrow"></viedoc-icon>
        </div>
        <div class="select__dropdown" @keydown=${this.handleDropdownKeydown}>
          <viedoc-menu
            id="menu"
            @v-select=${this.handleMenuSelect}
            @v-label-change=${this.handleMenuLabelChange}
            ?disabled=${this.disabled}
          >
            <slot @slotchange=${this.handleMenuSlotChange}></slot>
          </viedoc-menu>
        </div>
      </div>
    `;
    }
};
ViedocSelect.formAssociated = true;
ViedocSelect.styles = [
    css `
      :host {
        --viedoc-select-z-index: 5;
        display: block;
      }

      .select {
        font-family: var(--font-family-monument);
        color: var(--primary-text-color);
      }

      .select--disabled {
        pointer-events: none;
        color: var(--color-gray-50);
      }

      .select__combobox {
        box-sizing: border-box;
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
        background-color: var(--color-gray-10);
        transition: background-color 150ms;
        border-radius: 2.5rem;
        border: 1px solid transparent;
        padding: 0 1.125rem 0 1rem;
        height: 2.563rem;
        width: 100%;
        cursor: pointer;
        user-select: none;
      }

      .select--multiple .select__combobox {
        padding: 0.313rem 1.125rem 0.313rem 0.313rem;
        height: auto;
        min-height: 2.563rem; /* min-height and auto-height to allow growing when selecting multiple options */
      }

      .select__combobox:hover {
        background-color: var(--neutral-highlight-color);
      }

      .select__combobox:hover .select__label .secondary {
        color: var(--secondary-text-color);
      }

      .select--small .select__combobox {
        padding: 0 0.688rem 0 0.813rem;
        height: 2.25rem;
      }

      .select--small.select--multiple .select__combobox {
        padding: 0.188rem 0.688rem 0.188rem 0.25rem;
        height: auto;
        min-height: 2.25rem;
      }

      .select__label {
        display: flex;
        align-items: center;
        overflow: hidden;
        margin-inline-end: 0.5rem;
        font-size: var(--font-size-sm);
        gap: 0.313rem;
        width: 100%;
      }

      .select__label .secondary {
        color: var(--color-gray-30);
      }

      .select--multiple .select__label {
        flex-wrap: wrap;
      }

      .select--small .select__label {
        font-size: var(--font-size-xs);
      }

      .select--small.select--multiple .select__label {
        gap: 0.25rem;
      }

      .select__combobox:focus-within {
        outline: 5px auto Highlight;
        outline: 5px auto -webkit-focus-ring-color;
      }

      .select__label--content {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }

      .select__label--prefix,
      .select__label--suffix {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
      }

      .select__label--suffix {
        margin-inline-start: auto;
      }

      .select__label--prefix > * {
        margin-inline-end: 0.5rem;
        height: 1.5rem;
      }

      .select__label--suffix > * {
        height: 1.5rem;
      }

      .select--small .select__label--prefix > * {
        margin-inline-end: 0.4rem;
        height: 1.125rem;
      }

      .select--small .select__label--suffix > * {
        height: 1.125rem;
      }

      viedoc-icon[name='arrow'] {
        display: flex;
        justify-content: center;
        width: 0.982rem;
        flex: 0 0 auto;
        transform: rotate(-90deg);
        transform-origin: center;
        transition: transform 250ms;
      }

      .select--open viedoc-icon[name='arrow'] {
        transform: rotate(-270deg);
      }

      .select__dropdown {
        box-sizing: border-box;
        visibility: hidden;
        height: max-content;
        position: fixed;
        top: 0;
        left: 0;
        z-index: var(--viedoc-select-z-index);
      }

      .select--open .select__dropdown {
        visibility: visible;
      }

      .select--outline .select__combobox {
        background-color: var(--secondary-background-color);
        transition: background-color 150ms ease 0s;
        border-radius: 0.625rem;
        border: solid 1px var(--color-gray-30);
      }
    `,
];
__decorate([
    property({ type: Boolean })
], ViedocSelect.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], ViedocSelect.prototype, "label", void 0);
__decorate([
    property({ type: Boolean })
], ViedocSelect.prototype, "multiple", void 0);
__decorate([
    property({ type: String })
], ViedocSelect.prototype, "name", void 0);
__decorate([
    property({ type: String })
], ViedocSelect.prototype, "variant", void 0);
__decorate([
    property({ type: String })
], ViedocSelect.prototype, "size", void 0);
__decorate([
    property({
        converter: {
            fromAttribute: (value) => value.split(VALUE_DELIMITER),
            toAttribute: (value) => value.join(VALUE_DELIMITER),
        },
    })
], ViedocSelect.prototype, "value", void 0);
__decorate([
    property({ type: ElementInternals })
], ViedocSelect.prototype, "_internals", void 0);
__decorate([
    state()
], ViedocSelect.prototype, "isOpen", void 0);
__decorate([
    query('.select__dropdown')
], ViedocSelect.prototype, "dropdown", void 0);
__decorate([
    query('viedoc-menu')
], ViedocSelect.prototype, "menu", void 0);
__decorate([
    query('.select__combobox')
], ViedocSelect.prototype, "combobox", void 0);
__decorate([
    queryAssignedElements({ selector: 'viedoc-menu-item' })
], ViedocSelect.prototype, "menuItems", void 0);
ViedocSelect = __decorate([
    customElement('viedoc-select')
], ViedocSelect);
export { ViedocSelect };
